import { ColDef } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { OffHoursSchedule } from "@/services/cloudchipr.api";
import { OffHoursDataGridNameCell } from "@/components/pages/automations/off-hours/grid/cells/OffHoursDataGridNameCell";
import { OffHoursDataGridNextRunGridCell } from "@/components/pages/automations/off-hours/grid/cells/OffHoursDataGridNextRunGridCell";
import { AutomationsLastExecutionCell } from "@/components/pages/automations/common/components/last-execution-cell/AutomationsLastExecutionCell";
import { AutomationsFrequencyCell } from "@/components/pages/automations/common/components/grid/columns/AutomationsFrequencyCell";
import { AccountsWithPopover } from "@/components/common/accounts-with-popover/AccountsWithPopover";
import { AutomationsActivationSwitcher } from "@/components/pages/automations/common/components/grid/automation-activation-switcher/AutomationsActivationSwitcher";
import { agGridActionColumnDefaultColumnDef } from "@/components/pages/common/ag-grid/utils/constants/agGridActionColumnDefaultColumnDef";

import { OffHoursDataGridActionCell } from "@/components/pages/automations/off-hours/grid/cells/OffHoursDataGridActionCell";
import { agGridEmptyColumnToFitEmptySpace } from "@/components/pages/common/ag-grid/utils/constants/agGridEmptyColumnToFitEmptySpace";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { offHoursNextRunValueGetter } from "@/components/pages/automations/off-hours/utils/helpers/value-getters/offHoursNextRunValueGetter";
import { offHoursActionValueGetter } from "@/components/pages/automations/off-hours/utils/helpers/value-getters/offHoursActionValueGetter";
import { offHoursLastExecutionValueGetter } from "@/components/pages/automations/off-hours/utils/helpers/value-getters/offHoursLastExecutionValueGetter";
import { offHoursFrequencyValueGetter } from "@/components/pages/automations/off-hours/utils/helpers/value-getters/offHoursFrequencyValueGetter";
import { offHoursAccountValueGetter } from "@/components/pages/automations/off-hours/utils/helpers/value-getters/offHoursAccountValueGetter";
import { offHoursFrequencyComparator } from "@/components/pages/automations/off-hours/utils/helpers/comparators/offHoursFrequencyComparator";
import { createdByColumnDef } from "@/components/pages/common/ag-grid/components/columns/createdByColumnDef";
import { modifiedByColumnDef } from "@/components/pages/common/ag-grid/components/columns/modifiedByColumnDef";

export const offHoursGridColumnDefs: ColDef<OffHoursSchedule>[] = [
  {
    field: "name",
    headerName: "Name",
    pinned: "left",
    minWidth: 200,
    width: 320,
    suppressColumnsToolPanel: true,
    cellRenderer: OffHoursDataGridNameCell,
  },
  {
    field: "metadata.next_start_run_at",
    headerName: "Next Run",
    headerStyle: {
      paddingLeft: 24,
    },
    cellRenderer: OffHoursDataGridNextRunGridCell,
    valueGetter: offHoursNextRunValueGetter,
  },
  {
    field: "action",
    headerName: "Action",
    cellRenderer: ({ value }: CustomCellRendererProps<OffHoursSchedule>) => {
      if (!value) {
        return "-";
      }

      return <TypographyWithTooltip title={value} variant="body2" />;
    },
    valueGetter: offHoursActionValueGetter,
  },
  {
    field: "last_execution",
    headerName: "Last Execution",
    minWidth: 260,
    width: 260,
    maxWidth: 300,
    cellRenderer: ({ data }: CustomCellRendererProps<OffHoursSchedule>) => {
      if (!data) {
        return "-";
      }

      return (
        <AutomationsLastExecutionCell
          source="off_hours"
          name={data.name}
          timeZone={data.time_zone}
          lastExecution={data.last_execution}
        />
      );
    },
    valueGetter: offHoursLastExecutionValueGetter,
  },
  {
    field: "start_cron",
    headerName: "Frequency",
    comparator: offHoursFrequencyComparator,
    cellRenderer: ({ data }: CustomCellRendererProps<OffHoursSchedule>) => {
      if (!data) {
        return "-";
      }

      return (
        <AutomationsFrequencyCell
          nextRun={data?.metadata?.next_start_run_at ?? ""}
          timeZone={data.time_zone}
          cron={data.start_cron ?? ""}
        />
      );
    },
    valueGetter: offHoursFrequencyValueGetter,
  },
  {
    field: "accounts",
    headerName: "Account",
    cellRenderer: ({ data }: CustomCellRendererProps<OffHoursSchedule>) => {
      if (!data) {
        return "-";
      }

      return <AccountsWithPopover accounts={data.accounts} />;
    },
    valueGetter: offHoursAccountValueGetter,
  },
  createdByColumnDef,
  modifiedByColumnDef,
  agGridEmptyColumnToFitEmptySpace,

  {
    ...agGridActionColumnDefaultColumnDef,
    field: "status",
    headerName: "State",
    width: 70,
    minWidth: 70,
    maxWidth: 70,
    resizable: false,
    cellRenderer: ({ data }: CustomCellRendererProps<OffHoursSchedule>) => {
      if (!data) {
        return "-";
      }

      return <AutomationsActivationSwitcher schedule={data} type="off-hours" />;
    },
  } as ColDef,
  {
    ...agGridActionColumnDefaultColumnDef,
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    colId: "actions",
    cellDataType: false,
    cellRenderer: OffHoursDataGridActionCell,
  } as ColDef,
];
